import { useEffect, useRef, useState } from "react";
import { s } from "../style.js";
import type { Toast as ToastData } from "../hooks/useToast";

type ToastProps = Omit<ToastData, "createdAt"> & { onDismiss: (id: string) => void };

const DURATION = 4000;
const ANIM = 200;

export default function Toast({ id, message, type, onDismiss }: ToastProps) {
  const [closing, setClosing] = useState(false);
  const labelColor = type === "success" ? "#22c55e" : "#ec3013";
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      setClosing(true);
      setTimeout(() => dismissRef.current(id), ANIM);
    }, DURATION - ANIM);
    return () => clearTimeout(timerRef.current);
  }, [id]);

  const handleDismiss = () => {
    clearTimeout(timerRef.current);
    setClosing(true);
    setTimeout(() => dismissRef.current(id), ANIM);
  };

  return (
    <>
      <style>{`@keyframes toast-slide-in { from { transform: translateX(100%); } to { transform: translateX(0); } } @keyframes toast-slide-out { from { transform: translateX(0); } to { transform: translateX(120%); } } @keyframes toast-progress { from { width: 0%; } to { width: 100%; } }`}</style>
      <div
        role="status"
        style={{ ...s("position:relative; width:360px; box-sizing:border-box; display:flex; align-items:center; gap:14px; padding:20px 14px 18px 18px; background:var(--color-surface); border:2px solid var(--color-divider)"), overflow: "visible", animation: closing ? "toast-slide-out 200ms ease-in forwards" : "toast-slide-in 200ms ease-out" }}
      >
        <div style={{ ...s("position:absolute; top:0; left:-2px; padding:3px 12px; display:flex; align-items:center; justify-content:center; font-family:var(--font-heading); font-weight:800; font-size:12px; letter-spacing:0.1em; text-transform:uppercase; color:#fff; white-space:nowrap; line-height:1"), background: labelColor, transform: "translateY(-100%)" }}>{type}</div>
        <div style={s("flex:1; min-width:0; font-size:14px; font-family:var(--font-heading); font-weight:800; color:var(--color-text); word-break:break-word")}>{message}</div>
        <button onClick={handleDismiss} aria-label="Dismiss notification" style={s("all:unset; cursor:pointer; flex:none; font-size:20px; line-height:1; color:var(--color-neutral-600)")}>×</button>
        <div style={{ ...s("position:absolute; left:0; bottom:0; height:3px"), background: "#ec3013", animation: closing ? "none" : "toast-progress 4000ms linear forwards" }} />
      </div>
    </>
  );
}
